import React, { ReactNode, useState } from 'react';
import { Box, AppBar, Toolbar, Typography, Button, Drawer, List, ListItem, ListItemIcon, ListItemText, Divider, useTheme } from '@mui/material';
import {
  Menu as MenuIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  LocalMall as ProductsIcon,
  Dashboard as DashboardIcon,
  ExitToApp as LogoutIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface POSLayoutProps {
  children: ReactNode;
}

const drawerWidth = 240;

const POSLayout: React.FC<POSLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const navigateTo = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const menuItems = [
    { text: 'POS Dashboard', icon: <DashboardIcon />, path: '/pos' },
    { text: 'Unified POS', icon: <MenuIcon />, path: '/pos/unified' },
    { text: 'Order History', icon: <ReceiptIcon />, path: '/pos/orders' },
    { text: 'Products', icon: <ProductsIcon />, path: '/pos/products' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/pos/settings' },
  ];

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Button 
            color="inherit"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </Button>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Marina Point of Sale
          </Typography>
          <Button 
            color="inherit"
            onClick={() => navigate('/dashboard')}
            startIcon={<LogoutIcon />}
          >
            Exit POS
          </Button>
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant="temporary"
        open={open}
        onClose={handleDrawerToggle}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { 
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {menuItems.map((item) => (
              <ListItem 
                button 
                key={item.text}
                onClick={() => navigateTo(item.path)}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
          <Divider />
          <List>
            <ListItem 
              button 
              onClick={() => navigate('/dashboard')}
            >
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Exit POS" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
      
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: theme.palette.background.default
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default POSLayout; 